/**
 * `promo_banner` — one thing the seller wants to push (spec 42 R2; design 43 SF6).
 *
 * Presentation only: it applies no discount and creates no offer. Product brief
 * 19 item 5 rules out an offers engine and coupon codes entirely, so a banner
 * that *looked* like it carried a deal would be a lie the checkout can't honour.
 *
 * The dark card is `--ink` / `--paper`, not a gradient — brand §6 bans
 * gradients; the hero's scrim is the sole overlay exception.
 */
import type { LinkTarget } from "@flint/api-contract/store-home";
import { resolveTarget, isExternal, type TargetContext } from "./resolve-target.js";
import { SectionImage } from "./section-image.js";

export function PromoBanner({
  imageUrl,
  eyebrow,
  headline,
  body,
  bullets,
  cta,
  imageSide,
  ctx,
}: {
  imageUrl?: string | undefined;
  eyebrow?: string | undefined;
  headline: string;
  body?: string | undefined;
  bullets?: string[] | undefined;
  cta?: { label: string; target: LinkTarget } | undefined;
  imageSide: "left" | "right";
  ctx: TargetContext;
}) {
  const href = cta ? resolveTarget(cta.target, ctx) : null;
  // No image → the band is a single centred column (design 43 SF6 / wireframe),
  // not a left-aligned half. With an image it stays a side-by-side split.
  const centered = !imageUrl;

  const text = (
    <div
      className="flint-promo-text"
      style={centered ? { textAlign: "center", maxWidth: "44rem", marginInline: "auto" } : undefined}
    >
      {eyebrow ? (
        <p
          style={{
            // Brand §8: ember brightens on dark surfaces. --accent (ember-600)
            // only reaches 3.43:1 against --ink and fails AA at this size.
            color: "var(--brand)",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: "0.75rem",
          }}
        >
          {eyebrow}
        </p>
      ) : null}
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 500,
          fontSize: "1.875rem",
          lineHeight: 1.15,
          color: "var(--paper)",
          margin: 0,
        }}
      >
        {headline}
      </h2>
      {body ? (
        <p style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "var(--paper)", opacity: 0.9 }}>
          {body}
        </p>
      ) : null}
      {bullets && bullets.length > 0 ? (
        <ul style={{ marginTop: "1rem", listStyle: "none", padding: 0, display: "grid", gap: "0.5rem" }}>
          {bullets.map((b) => (
            <li
              key={b}
              style={{
                display: "flex",
                gap: "0.5rem",
                fontSize: "0.875rem",
                color: "var(--paper)",
                opacity: 0.9,
              }}
            >
              <span aria-hidden style={{ color: "var(--brand)" }}>
                ✓
              </span>
              {b}
            </li>
          ))}
        </ul>
      ) : null}
      {/* A dangling CTA drops; the card still carries its message (R3). */}
      {cta && href ? (
        <a
          href={href}
          {...(isExternal(cta.target) ? { rel: "noopener noreferrer", target: "_blank" } : {})}
          style={{
            display: "inline-flex",
            marginTop: "1.5rem",
            padding: "0.625rem 1.5rem",
            borderRadius: "var(--r-sm)",
            backgroundColor: "var(--accent)",
            color: "var(--accent-fg)",
            fontWeight: 600,
            fontSize: "0.9375rem",
            textDecoration: "none",
          }}
        >
          {cta.label}
        </a>
      ) : null}
    </div>
  );

  return (
    <section
      aria-label={headline}
      className={`flint-promo ${
        imageUrl ? (imageSide === "left" ? "flint-promo-img-left" : "") : "flint-promo-solo"
      }`}
      style={{ backgroundColor: "var(--ink)", borderRadius: "var(--r-lg)", overflow: "hidden" }}
    >
      {text}
      {/* No image → the text spans the card rather than leaving a void. */}
      {imageUrl ? (
        <div className="flint-promo-media">
          <SectionImage src={imageUrl} alt="" aspectRatio="16 / 9" radius="0" />
        </div>
      ) : null}
    </section>
  );
}
